import React, { useRef } from "react";
import { QRCode } from "react-qr-code";
import {
  MdAccessTime,
  MdCheckCircle,
  MdContentCopy,
  MdDownload,
  MdPrint,
  MdQrCode,
  MdRefresh,
  MdTrendingUp,
} from "react-icons/md";
import Button, { IconButton } from "components/button";
import Card, { CardHeader } from "components/card";
import { Select } from "components/fields";
import StatCard from "components/stat-card";
import { EmptyState } from "components/states";
import { useConfirm } from "components/dialog/ConfirmProvider";
import notify from "lib/notify";
import { formatNumber } from "utils/format";
import { formatRate, formatRatio } from "./metrics";

const SIZE_OPTIONS = [
  { value: 128, label: "Small (128x128)" },
  { value: 256, label: "Medium (256x256)" },
  { value: 512, label: "Large (512x512)" },
  { value: 1024, label: "Extra Large (1024x1024)" },
];

const STEPS = [
  {
    title: "Download or Print",
    body: "Download the QR code as an image or print it directly for physical distribution.",
  },
  {
    title: "Place Strategically",
    body: "Add to bulletins, welcome packets, lobby displays, or announcement slides.",
  },
  {
    title: "Visitors Scan & Respond",
    body: "Visitors scan with their phone camera and complete the survey instantly.",
  },
];

const TIPS = [
  "Use larger sizes for printed materials (512px or above)",
  "Ensure good contrast when printing",
  "Test the QR code after printing",
  "Place where visitors naturally gather",
];

const copyToClipboard = (text) => {
  navigator.clipboard.writeText(text);
  notify.success("Link copied");
};

const UrlRow = ({ label, url, copyLabel }) => (
  <div className="rounded-control bg-surface-raised p-4">
    <p className="mb-2 text-sm font-medium text-fg-muted">{label}</p>
    <div className="flex items-center gap-2">
      <code className="min-w-0 flex-1 truncate font-mono text-sm font-semibold text-fg">{url}</code>
      <IconButton
        icon={<MdContentCopy />}
        label={copyLabel}
        variant="secondary"
        size="sm"
        onClick={() => copyToClipboard(url)}
      />
    </div>
  </div>
);

const QrTab = ({ campaign, qrStats, qrSize, onQrSizeChange, generatingQr, onGenerateQrCode }) => {
  const confirm = useConfirm();
  const qrRef = useRef(null);

  const downloadQRCode = (format = "png") => {
    const svg = qrRef.current?.querySelector("svg");
    if (!svg) return;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const svgData = new XMLSerializer().serializeToString(svg);
    const img = new Image();

    canvas.width = qrSize;
    canvas.height = qrSize;

    img.onload = () => {
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, qrSize, qrSize);
      ctx.drawImage(img, 0, 0, qrSize, qrSize);

      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${campaign.name.replace(/\s+/g, "-").toLowerCase()}-qr-code.${format}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        notify.success("QR code downloaded");
      }, `image/${format}`);
    };

    img.src = "data:image/svg+xml;base64," + btoa(svgData);
  };

  const printQRCode = () => {
    const printWindow = window.open("", "_blank");
    const svg = qrRef.current?.querySelector("svg");
    if (!svg) return;

    const qrUrl = campaign.qrCodeShortUrl || campaign.qrCodeUrl;

    printWindow.document.write(`
      <html>
        <head>
          <title>QR Code - ${campaign.name}</title>
          <style>
            body {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
              margin: 0;
              font-family: Arial, sans-serif;
            }
            .container {
              text-align: center;
              padding: 40px;
            }
            h1 {
              margin-bottom: 10px;
              font-size: 24px;
            }
            .subtitle {
              margin-bottom: 30px;
              font-size: 16px;
              color: rgb(102, 102, 102);
            }
            .url {
              margin-bottom: 30px;
              font-size: 14px;
              color: rgb(51, 51, 51);
            }
            .qr-code {
              margin: 30px auto;
            }
            @media print {
              body { background: white; }
            }
          </style>
        </head>
        <body>
          <div class="container">
            <h1>${campaign.name}</h1>
            <div class="subtitle">Scan to take our survey</div>
            <div class="qr-code">${svg.outerHTML}</div>
            <div class="url">${qrUrl}</div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const handleRegenerate = async () => {
    const confirmed = await confirm({
      title: `Regenerate the QR code for "${campaign.name}"?`,
      message:
        "A new code and link will be created. Printed codes and shared links for the current QR code will stop working.",
      confirmLabel: "Regenerate",
      tone: "danger",
    });
    if (confirmed) onGenerateQrCode(true);
  };

  const hasQr = Boolean(campaign.qrCodeToken);

  return (
    <div className="flex flex-col gap-5">
      {qrStats && hasQr && (
        <section aria-labelledby="qr-stats-heading" className="flex flex-col gap-3">
          <h2 id="qr-stats-heading" className="text-heading">
            QR Code Statistics
          </h2>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard
              icon={MdQrCode}
              accent="sky"
              label="Total Scans"
              value={formatNumber(qrStats.totalScans || 0)}
              info="Times the QR code was scanned or the survey link was opened."
            />
            <StatCard
              icon={MdCheckCircle}
              accent="sage"
              label="Completed"
              value={formatNumber(qrStats.conversions || 0)}
              info="Scans that ended in a submitted survey."
            />
            <StatCard
              icon={MdTrendingUp}
              accent="gold"
              label="Conversion Rate"
              value={formatRate(qrStats.conversions, qrStats.totalScans)}
              caption={formatRatio("Completed", qrStats.conversions, qrStats.totalScans, "scans")}
              info="Share of scans that ended in a submitted survey."
            />
            <StatCard
              icon={MdAccessTime}
              accent="slate"
              label="Today"
              value={formatNumber(qrStats.todayScans || 0)}
              info="Scans so far today."
            />
          </div>
        </section>
      )}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <Card padding="md">
          <CardHeader title="Campaign QR Code" />

          {hasQr ? (
            <div className="mt-4 flex flex-col gap-6">
              <div className="flex flex-col gap-3">
                {campaign.qrCodeShortUrl && (
                  <UrlRow
                    label="Short URL (Recommended)"
                    url={campaign.qrCodeShortUrl}
                    copyLabel="Copy short URL"
                  />
                )}
                <UrlRow label="Full Survey URL" url={campaign.qrCodeUrl} copyLabel="Copy URL" />
              </div>

              {/* White backing keeps the code scannable in dark mode. */}
              <div
                ref={qrRef}
                className="flex justify-center overflow-auto rounded-control bg-white p-8"
              >
                <QRCode
                  value={campaign.qrCodeShortUrl || campaign.qrCodeUrl}
                  size={qrSize}
                  level="H"
                />
              </div>

              <Select
                label="QR Code Size"
                value={qrSize}
                onChange={(e) => onQrSizeChange(Number(e.target.value))}
                options={SIZE_OPTIONS}
              />

              <div className="flex flex-col gap-3">
                <Button
                  fullWidth
                  leftIcon={<MdDownload aria-hidden="true" />}
                  onClick={() => downloadQRCode("png")}
                >
                  Download PNG
                </Button>
                <Button
                  variant="secondary"
                  fullWidth
                  leftIcon={<MdPrint aria-hidden="true" />}
                  onClick={printQRCode}
                >
                  Print QR Code
                </Button>
                <Button
                  variant="secondary"
                  fullWidth
                  loading={generatingQr}
                  leftIcon={<MdRefresh aria-hidden="true" />}
                  onClick={handleRegenerate}
                >
                  {generatingQr ? "Regenerating…" : "Regenerate QR Code"}
                </Button>
              </div>
            </div>
          ) : (
            <EmptyState
              icon={MdQrCode}
              title="No QR Code Generated"
              description="Generate a QR code to allow visitors to take this survey without email."
              action={
                <Button
                  loading={generatingQr}
                  leftIcon={<MdQrCode aria-hidden="true" />}
                  onClick={() => onGenerateQrCode(false)}
                >
                  {generatingQr ? "Generating…" : "Generate QR Code"}
                </Button>
              }
            />
          )}
        </Card>

        <div className="flex flex-col gap-5">
          <Card padding="md">
            <CardHeader title="How to Use Your QR Code" />
            <ol className="mt-4 flex flex-col gap-4">
              {STEPS.map((step, index) => (
                <li key={step.title} className="rounded-control bg-surface-raised p-4">
                  <h3 className="mb-2 flex items-center gap-2 font-semibold text-fg">
                    <span
                      aria-hidden="true"
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-sm text-primary-fg"
                    >
                      {index + 1}
                    </span>
                    {step.title}
                  </h3>
                  <p className="text-sm text-fg-muted">{step.body}</p>
                </li>
              ))}
            </ol>
          </Card>

          <Card padding="md">
            <CardHeader title="Tips for Best Results" />
            <ul className="mt-4 list-disc space-y-2 pl-5 text-sm text-fg-muted marker:text-accent">
              {TIPS.map((tip) => (
                <li key={tip}>{tip}</li>
              ))}
            </ul>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default QrTab;
